/**
 * Human-readable screen names for native app analytics.
 *
 * In a Capacitor app every route renders inside the one native Activity
 * (Android) / view controller (iOS), so Firebase's automatic `screen_view`
 * would label every screen the same ("MainActivity") — you can't see the
 * user journey. We instead set an explicit, descriptive screen name on each
 * route change (see useScreenTracking + analytics.trackScreenView).
 *
 * Names are analyst-facing (they appear in the Firebase / GA4 console, not to
 * members), so they read as clear Title Case labels grouped by area:
 *   - marketing site pages,
 *   - the booking app screens ("app · …"),
 *   - the InBody / health results screens ("body · …"),
 *   - checkout outcomes.
 */

// Exact-match routes → screen name. Kept in sync with the <Route> table in
// App.tsx; a path with no entry falls back to a derived name.
const EXACT: Record<string, string> = {
  "/": "home",
  "/about": "about",
  "/pricing": "pricing",
  "/equipment": "equipment",
  "/experience/plunge": "experience · cold plunge",
  "/experience/sauna": "experience · sauna",
  "/experience/compress": "experience · compression",
  "/experience/scan": "experience · inbody scan",
  "/experience/redlight": "experience · red light",
  "/signup/membership": "signup · membership",
  "/signup/credits": "signup · credits",
  "/signup/private": "signup · private room",
  "/contact": "contact",
  "/visit": "visit",
  "/legal": "legal",
  "/terms": "terms",
  "/corporate-team-building": "corporate team building",
  "/birthday-party": "group booking · birthday party",
  "/team-recovery": "group booking · team recovery",
  "/date-night": "group booking · date night",
  "/gift-a-session": "group booking · gift a session",
  "/group-day-out": "group booking · group day out",
  "/run-club-recovery": "group booking · run club recovery",
  "/marathon-recovery": "group booking · marathon recovery",
  "/hyrox-recovery": "group booking · hyrox recovery",
  "/crossfit-recovery": "group booking · crossfit recovery",

  // Booking app (the signed-in experience) — grouped under "app · …".
  "/app": "app · today",
  "/dashboard": "app · today",
  "/app/equipment": "app · browse equipment",
  "/auth": "app · sign in",
  // /bookings is the picker for a NEW booking; /my-bookings is the list of the
  // ones you have. The labels used to be the other way round from the screens.
  "/bookings": "app · book a visit",
  "/my-bookings": "app · my bookings",
  "/session": "app · live session",
  "/wallet": "app · wallet",
  "/plans": "app · plans",
  "/profile": "app · profile",

  // Health / InBody results — grouped under "body · …".
  "/scan": "scan in",
  "/body": "body · inbody",
  "/body/inbody": "body · inbody",
  "/body/longevity": "body · longevity",
  "/body/health": "body · health",
  "/body/fitness": "body · fitness",
  "/body/sleep": "body · sleep",
  "/body/nutrition": "body · nutrition",
  "/body/test-results": "body · test results",
  "/goals": "goals",
  "/fitness-reset": "fitness reset",
  "/leaderboard": "leaderboard",
  "/health-settings": "health settings",

  // Account / operational.
  "/reset-password": "reset password",
  "/admin": "admin",
  "/door": "booking calendar · door display",
  "/frontdoor": "front door · wall display",
  "/room/inbody": "inbody room screen",
  "/payment/return": "checkout · payment result",
  "/payment/cancel": "checkout · payment cancelled",
};

/** Derive a readable screen name from any pathname. */
export function screenNameForPath(pathname: string): string {
  if (!pathname) return "unknown";
  // Normalise trailing slash (except the root).
  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;

  const exact = EXACT[path];
  if (exact) return exact;

  // Fallback: turn "/body/test-results" → "body · test results" so even an
  // un-mapped route still lands under a sensible group instead of the raw URL.
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return "home";
  return segments.map((s) => s.replace(/-/g, " ")).join(" · ");
}
